// S2-FE2: ComponentShowcase — visual inventory of all components
import { useState } from 'react';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Card } from '../components/Card';
import { MetricCard } from '../components/MetricCard';
import { GateIndicator } from '../components/GateIndicator';
import { VerdictBanner } from '../components/VerdictBanner';
import { ActionCard } from '../components/ActionCard';
import { ProgressBar } from '../components/ProgressBar';
import { StatusDot } from '../components/StatusDot';
import { Input } from '../components/Input';
import { Logo } from '../components/Logo';
import { Wordmark } from '../components/Wordmark';
import { QualityDimensionChart } from '../components/charts/QualityDimensionChart';
import { DonutChart, DonutSegment } from '../components/charts/DonutChart';
import { Histogram, HistogramBucket } from '../components/charts/Histogram';
import { TabSwitcher } from '../components/TabSwitcher';
import { EmptyState } from '../components/EmptyState';
// S2-CC20.6b.1.5: Call Center primitives.
import { StatusPill } from '../components/StatusPill';
import { FilterChip, FilterChipGroup } from '../components/FilterChip';
import { TreeNode } from '../components/TreeNode';
import type { BudgetNode } from '../types/llm';

// S2-LIB3: DonutChart showcase fixtures
// Populated (Model breakdown) — mirrors the s2-cc6 wireframe pivot 1.
const donutModelSegments: DonutSegment[] = [
  { id: 'sonnet', label: 'Sonnet', value: 85, color: 'var(--chart-1)' },
  { id: 'opus',   label: 'Opus',   value: 11, color: 'var(--chart-2)' },
  { id: 'haiku',  label: 'Haiku',  value:  4, color: 'var(--chart-3)' },
];
// Populated (Eval Type) — pivot 2 from the wireframe, 5 varied slices.
const donutEvalSegments: DonutSegment[] = [
  { id: 'full',  label: 'Full-skill eval', value: 62, color: 'var(--chart-1)' },
  { id: 'bare',  label: 'Bare eval',       value: 22, color: 'var(--chart-2)' },
  { id: 'ready', label: 'Readiness',       value:  9, color: 'var(--chart-4)' },
  { id: 'prod',  label: 'Production',      value:  5, color: 'var(--chart-3)' },
  { id: 'smoke', label: 'Smoke test',      value:  2, color: 'var(--chart-6)' },
];

// S2-LIB4: Histogram showcase fixtures
// Populated — token-distribution buckets mirroring the s2-cc6 wireframe.
const histPopulated: HistogramBucket[] = [
  { label: '0–5k', count: 2 },
  { label: '5–10k', count: 5 },
  { label: '10–20k', count: 9 },
  { label: '20–40k', count: 2 },
  { label: '40k+', count: 0 },
];
// All-zero variant — same labels, no data; exercises the empty overlay.
const histEmpty: HistogramBucket[] = histPopulated.map((b) => ({
  label: b.label,
  count: 0,
}));

// Sample quality dimensions (realistic evaluation data)
const sampleDimensions = [
  { id: 'A1', name: 'Benefit-Led', score: 0.92, threshold: 0.85, pass: true },
  { id: 'A2', name: 'Field Completeness', score: 0.88, threshold: 0.85, pass: true },
  { id: 'A3', name: 'Brand Voice', score: 0.78, threshold: 0.85, pass: false },
  { id: 'A4', name: 'Factual Accuracy', score: 0.95, threshold: 0.85, pass: true },
  { id: 'A5', name: 'SEO', score: 0.73, threshold: 0.85, pass: false },
  { id: 'A6', name: 'Tag Relevance', score: 0.91, threshold: 0.85, pass: true },
  { id: 'A7', name: 'Listing Complete', score: 0.86, threshold: 0.85, pass: true },
  { id: 'A8', name: 'Regulatory', score: 0.65, threshold: 0.85, pass: false },
];

// Styles for the showcase layout (page-level only, not component styles)
const styles: Record<string, React.CSSProperties> = {
  page: {
    maxWidth: 'var(--max-width-content)',
    margin: '0 auto',
    padding: 'var(--space-8)',
    fontFamily: 'var(--font-primary)',
  },
  title: {
    fontSize: 'var(--text-3xl)',
    fontWeight: 800,
    color: 'var(--text-primary)',
    marginBottom: 'var(--space-2)',
  },
  subtitle: {
    fontSize: 'var(--text-sm)',
    color: 'var(--text-muted)',
    marginBottom: 'var(--space-10)',
  },
  section: {
    marginBottom: 'var(--space-10)',
  },
  sectionLabel: {
    fontSize: 'var(--text-xs)',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.8px',
    color: 'var(--text-muted)',
    marginBottom: 'var(--space-4)',
    borderBottom: '1px solid var(--border-default)',
    paddingBottom: 'var(--space-2)',
  },
  row: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 'var(--space-3)',
    alignItems: 'center',
    marginBottom: 'var(--space-3)',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: 'var(--space-3)',
    marginBottom: 'var(--space-3)',
  },
  note: {
    fontSize: 'var(--text-2xs)',
    color: 'var(--text-muted)',
    fontStyle: 'italic',
    marginTop: 'var(--space-1)',
  },
};

export default function ComponentShowcase() {
  const [inputVal, setInputVal] = useState('');
  const [selectVal, setSelectVal] = useState('full');
  const [textareaVal, setTextareaVal] = useState('');
  // TabSwitcher showcase state — one piece of state per live example so each
  // segmented control is independently interactive in the gallery.
  const [tabPair, setTabPair] = useState('model');
  const [tabRange, setTabRange] = useState('7d');

  // S2-CC20.6b.1.5: FilterChip showcase state — three status chips + one
  // provider chip wired to local state so hovering/clicking is live.
  const [chipStatus, setChipStatus] = useState<Record<string, boolean>>({
    all: true,
    success: false,
    blocked: true,
  });
  const toggleStatus = (id: string) =>
    setChipStatus((s) => ({ ...s, [id]: !s[id] }));

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>Component Library</h1>
      <p style={styles.subtitle}>
        13 components. Every color, spacing, and radius from tokens.css.
      </p>

      {/* ---- 1. Button ---------------------------------------- */}
      <section style={styles.section}>
        <div style={styles.sectionLabel}>1. Button</div>
        <div style={styles.row}>
          <Button variant="primary" size="sm">Primary SM</Button>
          <Button variant="primary" size="md">Primary MD</Button>
          <Button variant="primary" size="lg">Primary LG</Button>
        </div>
        <div style={styles.row}>
          <Button variant="secondary">Secondary</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="danger">Danger</Button>
        </div>
        <div style={styles.row}>
          <Button variant="primary" loading>Loading</Button>
          <Button variant="primary" disabled>Disabled</Button>
        </div>
      </section>

      {/* ---- 2. Badge ----------------------------------------- */}
      <section style={styles.section}>
        <div style={styles.sectionLabel}>2. Badge</div>
        <div style={styles.row}>
          <Badge variant="success">Pass</Badge>
          <Badge variant="warning">Warn</Badge>
          <Badge variant="error">Fail</Badge>
          <Badge variant="info">Info</Badge>
          <Badge variant="neutral">Neutral</Badge>
          <Badge variant="primary">Primary</Badge>
          <Badge variant="signal">Signal</Badge>
        </div>
        <div style={styles.row}>
          <Badge variant="success" size="sm">SM</Badge>
          <Badge variant="success" size="md">MD</Badge>
        </div>
      </section>

      {/* ---- 3. Card ------------------------------------------ */}
      <section style={styles.section}>
        <div style={styles.sectionLabel}>3. Card</div>
        <div style={styles.grid}>
          <Card variant="standard" padding="compact">
            <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-xs)' }}>
              Standard / Compact
            </span>
          </Card>
          <Card variant="accent" padding="default">
            <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-xs)' }}>
              Accent / Default
            </span>
          </Card>
          <Card variant="elevated" padding="spacious">
            <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-xs)' }}>
              Elevated / Spacious
            </span>
          </Card>
        </div>
      </section>

      {/* ---- 4. MetricCard ------------------------------------ */}
      <section style={styles.section}>
        <div style={styles.sectionLabel}>4. MetricCard</div>
        <div style={styles.grid}>
          <MetricCard value="0.865" label="Quality Score" status="pass" mono />
          <MetricCard value="98s" label="Latency" status="warn" />
          <MetricCard value="5.2c" label="Cost / Run" status="fail" mono />
          <MetricCard value="29/29" label="Tasks Complete" status="neutral" />
        </div>
      </section>

      {/* ---- 4b. MetricCard sizes (S2-LIB1) ------------------- */}
      <section style={styles.section}>
        <div style={styles.sectionLabel}>MetricCard sizes</div>
        <div style={{ ...styles.grid, gridTemplateColumns: 'repeat(3, 1fr)' }}>
          {/* sm — current/default compact tile */}
          <MetricCard value="0.865" label="Quality" status="pass" mono />
          {/* md — secondary hero number */}
          <MetricCard value="52.8¢" label="Projected EOD" status="warn" size="md" />
          {/* hero — display KPI, always mono */}
          <MetricCard value="47.3¢" label="Spent today" status="pass" size="hero" />
        </div>
        <p style={styles.note}>
          sm (default) · md · hero — hero is always mono regardless of the mono prop.
        </p>
      </section>

      {/* ---- 5. GateIndicator --------------------------------- */}
      <section style={styles.section}>
        <div style={styles.sectionLabel}>5. GateIndicator</div>
        <div style={styles.row}>
          <GateIndicator gateId="G1" name="Quality" score={0.87} threshold={0.85} pass={true} blocking={true} />
          <GateIndicator gateId="G2" name="Attribution" score={0.72} threshold={0.80} pass={false} blocking={true} />
          <GateIndicator gateId="G3" name="Workflow" score={0.91} threshold={0.90} pass={false} blocking={false} />
        </div>
      </section>

      {/* ---- 6. VerdictBanner --------------------------------- */}
      <section style={styles.section}>
        <div style={styles.sectionLabel}>6. VerdictBanner</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <VerdictBanner
            verdict="READY"
            message="All gates passed. Agent ready for production."
            gatesPassed={5}
            gatesTotal={5}
          />
          <VerdictBanner
            verdict="CONDITIONAL"
            message="Non-blocking gate failed. Review recommended actions."
            gatesPassed={4}
            gatesTotal={5}
          />
          <VerdictBanner
            verdict="NOT_READY"
            message="Blocking gate G2 failed. Fix required before deploy."
            gatesPassed={3}
            gatesTotal={5}
          />
        </div>
      </section>

      {/* ---- 7. ActionCard ------------------------------------ */}
      <section style={styles.section}>
        <div style={styles.sectionLabel}>7. ActionCard</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <ActionCard
            actionType="content_tune"
            knobId="K-BRAND-VOICE"
            title="Strengthen brand vocabulary in SKILL.md"
            description="Brand voice score (A3) at 0.78, below 0.85 threshold. Add PetUp-specific tone examples."
            severity="high"
          />
          <ActionCard
            actionType="knob_addition"
            knobId="K-SEO-KEYWORDS"
            title="Add SEO keyword injection knob"
            description="SEO dimension (A5) consistently underperforms. A dedicated knob would allow keyword targeting."
            severity="medium"
          />
          <ActionCard
            actionType="parameter_change"
            knobId="K-TEMPERATURE"
            title="Reduce temperature for regulatory content"
            description="Regulatory score (A8) variance too high. Lower temperature for deterministic outputs."
            severity="low"
          />
        </div>
      </section>

      {/* ---- 8. ProgressBar ----------------------------------- */}
      <section style={styles.section}>
        <div style={styles.sectionLabel}>8. ProgressBar</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', maxWidth: '400px' }}>
          <ProgressBar value={87} variant="primary" size="md" />
          <ProgressBar value={92} variant="success" size="md" />
          <ProgressBar value={65} variant="warning" size="sm" />
          <ProgressBar value={30} variant="error" size="sm" />
        </div>
      </section>

      {/* ---- 9. StatusDot ------------------------------------- */}
      <section style={styles.section}>
        <div style={styles.sectionLabel}>9. StatusDot</div>
        <div style={styles.row}>
          <StatusDot status="ready" label="Ready" />
          <StatusDot status="not-ready" label="Not Ready" />
          <StatusDot status="conditional" label="Conditional" />
          <StatusDot status="evaluating" label="Evaluating..." />
          <StatusDot status="ready" />
        </div>
      </section>

      {/* ---- 10. Input ---------------------------------------- */}
      <section style={styles.section}>
        <div style={styles.sectionLabel}>10. Input</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)', maxWidth: '500px' }}>
          <Input
            label="Product Name"
            type="text"
            placeholder="e.g. Royal Canin Adult"
            helper="Test product identifier"
            value={inputVal}
            onChange={setInputVal}
          />
          <Input
            label="Config Preset"
            type="select"
            value={selectVal}
            onChange={setSelectVal}
            options={[
              { value: 'bare', label: 'Bare (no skill)' },
              { value: 'full', label: 'Full Stack' },
              { value: 'no-tools', label: 'No Tools' },
            ]}
            helper="Evaluation config"
          />
          <div style={{ gridColumn: '1 / -1' }}>
            <Input
              label="Notes"
              type="textarea"
              placeholder="Add evaluation notes..."
              rows={3}
              value={textareaVal}
              onChange={setTextareaVal}
            />
          </div>
        </div>
      </section>

      {/* ---- 11. Logo ----------------------------------------- */}
      <section style={styles.section}>
        <div style={styles.sectionLabel}>11. Logo</div>
        <div style={{ ...styles.row, color: 'var(--primary-400)' }}>
          <Logo size="sm" />
          <Logo size="md" />
          <Logo size="lg" />
          <Logo size="xl" />
        </div>
        <p style={styles.note}>Color inherited from parent via currentColor</p>
      </section>

      {/* ---- 12. Wordmark -------------------------------------- */}
      <section style={styles.section}>
        <div style={styles.sectionLabel}>12. Wordmark</div>
        <div style={styles.row}>
          <Wordmark size="sm" />
          <Wordmark size="md" />
          <Wordmark size="lg" />
          <Wordmark size="xl" />
        </div>
        <p style={styles.note}>Dark theme (default) shown above</p>
      </section>

      {/* ---- 14. TabSwitcher (S2-LIB2) ------------------------ */}
      <section style={styles.section}>
        <div style={styles.sectionLabel}>14. TabSwitcher</div>
        {/* 2-tab — matches the Model / Eval Type pattern on LLM Control Center */}
        <div style={styles.row}>
          <TabSwitcher
            tabs={[
              { id: 'model', label: 'Model' },
              { id: 'eval', label: 'Eval Type' },
            ]}
            activeId={tabPair}
            onChange={setTabPair}
            ariaLabel="Section 3 view"
          />
        </div>
        {/* 4-tab — proves the component handles >2 tabs cleanly */}
        <div style={styles.row}>
          <TabSwitcher
            tabs={[
              { id: 'today', label: 'Today' },
              { id: '7d', label: '7 Days' },
              { id: '30d', label: '30 Days' },
              { id: 'all', label: 'All Time' },
            ]}
            activeId={tabRange}
            onChange={setTabRange}
            size="md"
            ariaLabel="Time range"
          />
        </div>
        <p style={styles.note}>
          Roving tabindex + arrow keys (wrap), Home/End, Enter/Space activates. Token-driven styling.
        </p>
      </section>

      {/* ---- 13. QualityDimensionChart ------------------------ */}
      <section style={styles.section}>
        <div style={styles.sectionLabel}>13. QualityDimensionChart</div>
        <Card variant="standard" padding="default">
          <QualityDimensionChart dimensions={sampleDimensions} threshold={0.85} />
        </Card>
        <p style={styles.note}>
          Blue = pass, amber = near threshold, red = fail. Dashed line = 0.85 threshold.
        </p>
      </section>

      {/* ---- 15. DonutChart (S2-LIB3) -------------------------- */}
      <section style={styles.section}>
        <div style={styles.sectionLabel}>15. DonutChart</div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(240px, 1fr))',
            gap: 'var(--space-6)',
            alignItems: 'start',
          }}
        >
          {/* Example 1 — model breakdown, 3 slices, chart-1/2/3 palette. */}
          <Card variant="standard" padding="default">
            <DonutChart
              segments={donutModelSegments}
              centerValue="47.3¢"
              centerLabel="today"
            />
          </Card>
          {/* Example 2 — eval-type breakdown, 5 varied slices. */}
          <Card variant="standard" padding="default">
            <DonutChart
              segments={donutEvalSegments}
              centerValue="47.3¢"
              centerLabel="today"
            />
          </Card>
          {/* Example 3 — empty state. No legend, muted center message. */}
          <Card variant="standard" padding="default">
            <DonutChart segments={[]} emptyMessage="No calls today" />
          </Card>
        </div>
        <p style={styles.note}>
          Staggered segment animation on mount/change. Legend becomes clickable
          when onSegmentClick is provided. Empty = track only, no legend.
        </p>
      </section>

      {/* ---- 17. EmptyState (S2-LIB5) -------------------------- */}
      <section style={styles.section}>
        <div style={styles.sectionLabel}>17. EmptyState</div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(280px, 1fr))',
            gap: 'var(--space-6)',
            alignItems: 'start',
          }}
        >
          {/* Example 1 — dashed (default) with the default bar-chart icon
              and a numeric detail line. Mirrors the "waiting for signal"
              treatment used inside Section 3 of the LLM Control Center. */}
          <EmptyState
            title="Need >=5 calls for distribution signal"
            detail="2 / 5 calls today"
          />
          {/* Example 2 — solid variant, no detail. Used for top-level
              empty panels (e.g. "No jobs yet" at the list root). */}
          <EmptyState title="No jobs yet" variant="solid" />
        </div>
        <p style={styles.note}>
          Dashed = "waiting for signal" (transparent). Solid = empty list
          (surface-2 fill). Default bar-chart SVG; pass `icon` to override.
        </p>
      </section>

      {/* ---- 18. StatusPill (S2-CC20.6b.1.5) ------------------- */}
      <section style={styles.section}>
        <div style={styles.sectionLabel}>18. StatusPill</div>
        <div style={styles.row}>
          {/* success bucket */}
          <StatusPill status="success" />
          {/* warning bucket — 3 ENUMs all map to amber */}
          <StatusPill status="rate_limited" />
          <StatusPill status="gateway_paused" />
          <StatusPill status="timeout" />
          {/* copper bucket — intentional denials */}
          <StatusPill status="budget_blocked" />
          <StatusPill status="policy_blocked" />
          {/* error bucket — hard failures */}
          <StatusPill status="auth_failed" />
          <StatusPill status="provider_error" />
        </div>
        <div style={styles.row}>
          <StatusPill status="success" size="sm" />
          <StatusPill status="budget_blocked" size="sm" />
        </div>
        <p style={styles.note}>
          8 ENUMs → 4 color groups (success / warning / copper / error). Hover any pill to see the raw ENUM in the title tooltip.
        </p>
      </section>

      {/* ---- 19. FilterChip (S2-CC20.6b.1.5) ------------------- */}
      <section style={styles.section}>
        <div style={styles.sectionLabel}>19. FilterChip</div>
        <div style={styles.row}>
          <FilterChipGroup label="Status">
            <FilterChip
              label="All"
              selected={chipStatus.all}
              onToggle={() => toggleStatus('all')}
              count={50}
            />
            <FilterChip
              label="Success"
              selected={chipStatus.success}
              onToggle={() => toggleStatus('success')}
              count={44}
            />
            <FilterChip
              label="Blocked"
              selected={chipStatus.blocked}
              onToggle={() => toggleStatus('blocked')}
              count={4}
            />
          </FilterChipGroup>
        </div>
        <p style={styles.note}>
          Toggleable chips inside a labelled group. Count badge is optional; count=0 still renders.
        </p>
      </section>

      {/* ---- 20. TreeNode (S2-CC20.6b.1.5) --------------------- */}
      <section style={styles.section}>
        <div style={styles.sectionLabel}>20. TreeNode</div>
        {/* 3-level budget tree fixture:
              platform (3.8%) → role → role_instance (77%) → agent_instance leaf (103%, over).
            Mirrors the canonical wireframe's Section 8 budget tree so visual
            QA can side-by-side the component against the wireframe. */}
        <Card variant="standard" padding="default">
          <TreeNode
            node={
              {
                scope_type: 'platform',
                scope_id: 'PLATFORM',
                period_type: 'daily',
                period_start: '2026-04-19',
                limit_cents: 500,
                spent_cents: 19,
                pct_used: 3.8,
                hard_stop: true,
                status: 'active',
                children: [
                  {
                    scope_type: 'role',
                    scope_id: 'content-writer',
                    period_type: 'daily',
                    period_start: '2026-04-19',
                    limit_cents: 20,
                    spent_cents: 7.7,
                    pct_used: 38.3,
                    hard_stop: true,
                    status: 'active',
                    children: [
                      {
                        scope_type: 'role_instance',
                        scope_id: 'inst-petup-cw',
                        period_type: 'daily',
                        period_start: '2026-04-19',
                        limit_cents: 10,
                        spent_cents: 7.7,
                        pct_used: 77,
                        hard_stop: false,
                        status: 'active',
                        children: [
                          {
                            scope_type: 'agent_instance',
                            scope_id: 'listing-writer-petup',
                            period_type: 'daily',
                            period_start: '2026-04-19',
                            limit_cents: 5,
                            spent_cents: 5.2,
                            pct_used: 103,
                            hard_stop: false,
                            status: 'active',
                            children: [],
                          },
                        ],
                      },
                    ],
                  },
                ],
              } as BudgetNode
            }
          />
        </Card>
        <p style={styles.note}>
          Recursive budget tree. Platform + role auto-expanded; role_instance click-to-expand. Leaf agent_instance at 103% shows warn-only breach (🔓 unlocked + red pct label).
        </p>
      </section>

      {/* ---- 16. Histogram (S2-LIB4) --------------------------- */}
      <section style={styles.section}>
        <div style={styles.sectionLabel}>16. Histogram</div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(280px, 1fr))',
            gap: 'var(--space-6)',
            alignItems: 'start',
          }}
        >
          {/* Example 1 — populated token distribution with caption. */}
          <Card variant="standard" padding="default">
            <Histogram
              buckets={histPopulated}
              caption="peak: 35.4k tokens"
            />
          </Card>
          {/* Example 2 — all-zero buckets render rows + overlay. */}
          <Card variant="standard" padding="default">
            <Histogram
              buckets={histEmpty}
              emptyMessage="No calls today"
            />
          </Card>
        </div>
        <p style={styles.note}>
          Bar widths animate from 0 on mount/change. Zero-count buckets show
          label + "0" with an empty track. All-zero overlays emptyMessage on
          top of the (empty) rows.
        </p>
      </section>
    </div>
  );
}
